import { Stack, Typography } from '@mui/material';
import React, { useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import usePaging from 'new/state/paging/repository/usePaging';
import { ROUTE_SOURCES } from 'new/constants/pathnames';
import { ResourceEditContext } from 'js/components/ResourceEditProvider/ResourceEditContext';
import ResourceEditProvider from 'js/components/ResourceEditProvider/ResourceEditProvider';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { editModalsEnabled } from 'new/constants/featureFlags';
import GuideListSteps from './GuideListSteps';
import GuideCTA from './GuideCTA';
import GuideListSources from './GuideListSources';
import styles from './PipelinesEmptyListGuide.module.scss';
import flowTypes from 'new/components/flows/types/flowTypes';
import { useRestrictedTiers } from 'new/hooks/useRestrictedTiers';

const PipelinesEmptyListGuideInternal = () => {
  const { metaInfoById, commonConnectors: topConnectors } = usePaging();
  const history = useHistory();
  const { setAction } = useContext(ResourceEditContext);
  const { hasExpressTierLimitedAccess } = useRestrictedTiers();

  const handleSourceSelection = (connectionKey: string | null) => {
    const flowType = flowTypes.streaming;
    const modalConfig = editModalsEnabled() as any;
    const isModalEnabled = modalConfig?.source?.[flowType];

    if (isModalEnabled) {
      setAction?.({
        type: 'create',
        resource: RESOURCE_TYPE_SOURCE,
        flowType: flowType,
        queryParams: connectionKey
          ? {
              selectedConnection: connectionKey,
            }
          : {},
      });
    } else {
      // Fallback to URL navigation if modals are disabled
      const url = connectionKey
        ? `${ROUTE_SOURCES}/new?connection=${connectionKey}`
        : `${ROUTE_SOURCES}/new`;
      history.push(url);
    }
  };

  const topSourcesList = useMemo(() => {
    const commonConnectorsConfig = topConnectors || {};
    return Object.entries(commonConnectorsConfig)
      .map(([key, config]) => {
        const connectorConfig = typeof config === 'object' && config !== null ? config : {};
        const metaInfo = metaInfoById[key] || {};

        return {
          key,
          ...connectorConfig,
          ...metaInfo,
        };
      })
      .filter((source) => source.displayName);
  }, [topConnectors, metaInfoById]);

  return (
    <Stack className={styles.container}>
      <Stack gap="24px" alignItems="center">
        <Typography variant="body1" className={styles.title}>
          Connect Your First Data Source
        </Typography>
        <Typography variant="bodyLarge" className={styles.subtitle}>
          {hasExpressTierLimitedAccess ? (
            <>
              Get started in minutes by connecting to your existing systems with{' '}
              <a href="https://express.dev" target="_blank" rel="noopener noreferrer">
                Express
              </a>
              . No coding required - just prompt and create.
            </>
          ) : (
            'Get started in minutes by connecting to your existing systems. No coding required - just point, click, and sync.'
          )}
        </Typography>
      </Stack>
      <GuideListSteps />
      {!hasExpressTierLimitedAccess && (
        <GuideCTA onCsvUploadClick={() => handleSourceSelection('file_upload')} />
      )}
      <GuideListSources
        sources={topSourcesList}
        onSourceSelection={handleSourceSelection}
        hasExpressTierLimitedAccess={hasExpressTierLimitedAccess}
      />
    </Stack>
  );
};

// Wrapper component with ResourceEditProvider
const PipelinesEmptyListGuide = () => {
  return (
    <ResourceEditProvider>
      <PipelinesEmptyListGuideInternal />
    </ResourceEditProvider>
  );
};

export default PipelinesEmptyListGuide;
